// React
import { Fragment, useContext, useEffect, useState } from "react";

// Components
import { Card, BaseTable, Loading, DateInput, Select, PaginationData, TableSorting, TableHeaderSort } from "components";
import moment from "moment";
import { PaginationState } from "types";
import { APPROVAL_OPTIONS, default_pagination_value, useLocation } from "utils";
import { useList } from "./utils";
import { TableBody } from "./components";
import { DataContext } from "../utils/contexts";
import { FilterColumn } from "../components";

export interface FillType extends PaginationState {
  start_date?: string;
  end_date?: string;
  location_id?: string;
  approved?: string;
  source?: string;
}

export function ItemsAdjustment(): JSX.Element {
  const [pagination, setPagination] = useState<FillType>({
    ...default_pagination_value,
    start_date: moment().startOf("month").format("YYYY-MM-DD"),
    end_date: moment().endOf("month").format("YYYY-MM-DD"),
    location_id: "0",
    approved: "0",
    source: "0",
  });

  const { data: location, isLoading: loadingLocation } = useLocation();
  const { data, isLoading, refetch } = useList(pagination);
  const {setFill, setActiveColumn } = useContext(DataContext);

  const sourceDropdown = [
    { label: "ALL", value: "0" },
    { label: "COUNT ADJUSTMENT", value: "1" },
    { label: "DIRECT ADJUSTMENT", value: "2" },
  ];

  const header: TableSorting[] = [
    { title: "ADJUSTMENT ID#", value: "trx_code", isActive: true },
    { title: "REF COUNT ID#", value: "count_code", isActive: true },
    { title: "DATE", value: "transaction_date", isActive: true },
    { title: "LOCATION", value: "location_name", isActive: true },
    { title: "TRADER", value: "employee_name", isActive: true },
    { title: "APPROVED", value: "approve_status", isActive: true },
    { title: "MEMO", value: "memo", isActive: true },
    { title: "SOURCE", value: "source", isActive: true },
    { title: "STOCK IN", value: "stock_in", isActive: true },
    { title: "STOCK OUT", value: "stock_out", isActive: true },
    { title: "AMOUNT ADJUSTMENT", value: "amount", isActive: true },
  ];

  const [tableActive, setTableActive] = useState<TableSorting[]>(header);


  useEffect(() => {
    setActiveColumn && setActiveColumn(tableActive);
    setFill({ 
      dateFrom: pagination.start_date, 
      dateTo: pagination.end_date ,
      approval_status: pagination.approved,
      location_id: pagination.location_id,
      source: pagination.source
    });
  }, [
    setFill,
    pagination.start_date,
    pagination.end_date,
    pagination.approved,
    pagination.location_id,
    pagination.source,
    setActiveColumn,
    tableActive
  ]);



  return (
    <Fragment>
      <section className="my-2">
        <section className="flex flex-col gap-3">
          <Card>
            <Card.Body>
              <section className="lg:flex lg:items-end gap-3 mt-2 w-full">
                <DateInput
                  label="START DATE"
                  wrapperClassName="lg:w-[180px]"
                  onChange={(date) =>
                    setPagination({
                      ...pagination,
                      start_date: moment(date).format("YYYY-MM-DD"),
                    })
                  }
                  selected={
                    pagination.start_date ? moment(pagination.start_date).toDate() : null
                  }
                />

                <DateInput
                  label="END DATE"
                  wrapperClassName="lg:w-[180px]"
                  onChange={(date) =>
                    setPagination({
                      ...pagination,
                      end_date: moment(date).format("YYYY-MM-DD"),
                    })
                  }
                  selected={
                    pagination.end_date ? moment(pagination.end_date).toDate() : null
                  }
                />

                <Select
                  label="LOCATION"
                  controlClass="lg:w-[180px]"
                  width
                  options={[{ label: "All", value: "0" }, ...location]}
                  value={[{ label: "All", value: "0" }, ...location].find(
                    (item) => item.value === pagination.location_id
                  )}
                  isLoading={loadingLocation}
                  onChange={(e) => {
                    if (e) {
                      setPagination({ ...pagination, location_id: e.value });
                    }
                  }}
                />

                <Select
                  label="APPROVED"
                  controlClass="lg:w-[180px]"
                  width
                  options={[{ label: "All", value: "0" }, ...APPROVAL_OPTIONS]}
                  value={[{ label: "All", value: "0" }, ...APPROVAL_OPTIONS].find(
                    (item) => item.value === pagination.location_id
                  )}
                  onChange={(e) => {
                    if (e) {
                      setPagination({ ...pagination, approved: e.value });
                    }
                  }}
                />

                <Select
                  label="SOURCE"
                  controlClass="lg:w-[180px]"
                  width
                  options={sourceDropdown}
                  value={sourceDropdown.find((item) => item.value === pagination.source)}
                  onChange={(e) => {
                    if (e) {
                      setPagination({ ...pagination, source: e.value });
                    }
                  }}
                />

                <FilterColumn
                  dataFilter={tableActive}
                  setTableActive={setTableActive}
                />

              </section>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <div className="overflow-auto max-h-[calc(100vh-600px)]">
                <BaseTable className="border-collapse">
                  <thead className="sticky top-0 z-[0] !bg-black !text-white">
                    <tr>
                      <th>NO</th>

                      <TableHeaderSort
                        title={tableActive}
                      />

                      <th>ACTION</th>
                    </tr>
                  </thead>

                  <tbody>
                    {isLoading || data.payload?.length === 0 ? (
                      <tr>
                        <td colSpan={14}>
                          <Loading loading={isLoading} errorText={isLoading ? "" : "No data available"} />
                        </td>
                      </tr>
                    ) : data.payload?.map((item: any, index: number) => {
                      return (
                        <TableBody
                          index={index}
                          data={item}
                          pagination={pagination}
                          refetch={refetch}
                          tableActive={tableActive}
                        />
                      )
                    })
                    }
                  </tbody>
                </BaseTable>
              </div>
              <div className="mt-3">
                <PaginationData data={data} pagination={pagination} setPagination={setPagination} />
              </div>
            </Card.Body>
          </Card>
        </section>
      </section>
    </Fragment>
  );
}
